import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { emptyBrandDraft, useCreateBrand, type CreatedBrand } from '../../hooks/useCreateBrand';
import { Button } from '../ui/button';
import { DialogBody, DialogFooter, DialogTitle } from '../ui/dialog';
import { BrandFormFields } from './BrandFormFields';

/**
 * Inline "create brand" view that replaces a dialog's own content — no modal-on-modal.
 * Render it in place of the host form and swap back via `onCancel` / `onCreated`.
 */
export function CreateBrandStep({
	initialName,
	onCancel,
	onCreated,
}: {
	/** Prefill the name (e.g. the search text typed in the brand selector). */
	initialName?: string;
	onCancel: () => void;
	/** `brand` is set when the new id could be resolved. */
	onCreated: (brand?: CreatedBrand) => void;
}) {
	const [draft, setDraft] = useState({ ...emptyBrandDraft, name: initialName ?? '' });
	const { create, loading } = useCreateBrand();

	const handleSubmit = async () => {
		if (!draft.name.trim() || loading) return;
		const { success, brand } = await create(draft);
		if (success) onCreated(brand);
	};

	return (
		<>
			<div className="flex shrink-0 items-center gap-2 px-5 pt-5 pb-3">
				<button
					type="button"
					onClick={onCancel}
					disabled={loading}
					className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
				>
					<ArrowLeft size={15} />
				</button>
				<div>
					<DialogTitle className="text-lg font-medium text-text-1">
						Create brand
					</DialogTitle>
				</div>
			</div>

			<DialogBody className="scroll-thin max-h-[65vh] p-5">
				<BrandFormFields value={draft} onChange={setDraft} autoFocus />
			</DialogBody>

			<DialogFooter>
				<Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
					Cancel
				</Button>
				<Button
					type="button"
					onClick={handleSubmit}
					disabled={!draft.name.trim() || loading}
				>
					{loading ? 'Creating…' : 'Create brand'}
				</Button>
			</DialogFooter>
		</>
	);
}
